/**
 * Code-owned registry for functional forms — the single source of truth for
 * field keys, labels, options, requiredness, and tones, keyed by
 * formType × formVariant × formVersion. It drives picker rendering (weekly
 * form), route validation (which enums are required for a variant), and flyover
 * display (value → human label, value → tone). Pure and client-safe: no DB
 * imports.
 *
 * Tone follows ADR-0004 (colour = ordered severity gradation, never category):
 * `warn` (sunlight) and `risk` (orange) attach to specific enum *values*;
 * everything else is `quiet` (sand). Tone is always paired with a text label by
 * the UI — never colour-only.
 */

import {
  type FormClinicalDirectorAttention,
  type FormConfidence,
  type FormCounselingDevelopment,
  type FormNarrativeKey,
  type FormResponseJson,
  type FormStatus,
  type FormSupportTrack,
  type FormTrajectory,
  type FormType,
  type FormVariant,
} from "./form-types.js";

/** Current shipped form version. Existing snapshots keep their stored version. */
export const CURRENT_FORM_VERSION = 1;

export type FormTone = "quiet" | "warn" | "risk";

/** Design tokens per tone (take-2 comp). UI maps these to Tailwind classes. */
export const FORM_TONE_TOKENS: Record<FormTone, { bg: string; fg: string }> = {
  quiet: { bg: "sand-100", fg: "sand-600" },
  warn: { bg: "sunlight-100", fg: "sunlight-500" },
  risk: { bg: "orange-100", fg: "orange-500" },
};

export type FormFieldKind = "enum" | "text" | "textarea";

export interface FormEnumOption {
  value: string;
  label: string;
}

export interface FormFieldDef {
  /** Enum fields map to a snapshot column; text fields map to a response key. */
  key: string;
  label: string;
  kind: FormFieldKind;
  required: boolean;
  /** Present for enum fields; drives picker options and label lookup. */
  options?: FormEnumOption[];
  /** Inline hint shown under the label (e.g. Action plan's "plain text"). */
  hint?: string;
  /** Enum tone resolver: value → tone. Non-enum fields omit this. */
  toneFor?: (value: string) => FormTone;
}

// --- Value → label (human-readable display strings) -----------------------

const STATUS_LABELS: Record<FormStatus, string> = {
  maintenance_rock_star: "Maintenance Rock Star",
  active_growth: "Active Growth",
  watch_list: "Watch List",
  at_risk: "At Risk",
};
const TRAJECTORY_LABELS: Record<FormTrajectory, string> = {
  improving: "Improving",
  stable: "Stable",
  declining: "Declining",
};
const CONFIDENCE_LABELS: Record<FormConfidence, string> = {
  high: "High",
  moderate: "Moderate",
  low: "Low",
  concerning: "Concerning",
};
const COUNSELING_LABELS: Record<FormCounselingDevelopment, string> = {
  on_track: "On track",
  minor_gaps: "Minor gaps",
  significant_gaps: "Significant gaps",
};
const CD_ATTENTION_LABELS: Record<FormClinicalDirectorAttention, string> = {
  no: "No",
  monitor: "Monitor",
  yes: "Yes",
};

/**
 * Every enum value across the weekly form maps to a unique label (values are
 * globally unique across the five enums), so a single lookup is unambiguous.
 */
const FORM_VALUE_LABELS: Record<string, string> = {
  ...STATUS_LABELS,
  ...TRAJECTORY_LABELS,
  ...CONFIDENCE_LABELS,
  ...COUNSELING_LABELS,
  ...CD_ATTENTION_LABELS,
};

/** Human label for an enum value; falls back to the raw value if unknown. */
export function formValueLabel(value: string | null | undefined): string {
  if (!value) return "";
  return FORM_VALUE_LABELS[value] ?? value;
}

// --- Value → tone (ordered severity, ADR-0004) ----------------------------

/**
 * Tone map keyed by enum value. `warn` = Watch List / Monitor / Minor gaps /
 * Low; `risk` = At Risk / Yes / Significant gaps / Concerning / Declining;
 * every other value is `quiet`. Values are globally unique across the enums.
 */
const FORM_VALUE_TONES: Record<string, FormTone> = {
  // status
  watch_list: "warn",
  at_risk: "risk",
  // trajectory
  declining: "risk",
  // overall confidence
  low: "warn",
  concerning: "risk",
  // counseling development
  minor_gaps: "warn",
  significant_gaps: "risk",
  // clinical director attention
  monitor: "warn",
  yes: "risk",
};

/** Tone for an enum value; anything not in the map is `quiet`. */
export function toneForFormValue(value: string | null | undefined): FormTone {
  if (!value) return "quiet";
  return FORM_VALUE_TONES[value] ?? "quiet";
}

function toOptions<T extends string>(
  labels: Record<T, string>,
): FormEnumOption[] {
  return (Object.keys(labels) as T[]).map((value) => ({
    value,
    label: labels[value],
  }));
}

// --- Field defs -----------------------------------------------------------

const STATUS_FIELD: FormFieldDef = {
  key: "status",
  label: "Status",
  kind: "enum",
  required: true,
  options: toOptions(STATUS_LABELS),
  toneFor: toneForFormValue,
};
const TRAJECTORY_FIELD: FormFieldDef = {
  key: "trajectory",
  label: "Trajectory",
  kind: "enum",
  required: true,
  options: toOptions(TRAJECTORY_LABELS),
  toneFor: toneForFormValue,
};
const CONFIDENCE_FIELD: FormFieldDef = {
  key: "overallConfidence",
  label: "Overall RD confidence",
  kind: "enum",
  required: true,
  options: toOptions(CONFIDENCE_LABELS),
  toneFor: toneForFormValue,
};
const COUNSELING_FIELD: FormFieldDef = {
  key: "counselingDevelopment",
  label: "Counseling development",
  kind: "enum",
  required: true,
  options: toOptions(COUNSELING_LABELS),
  toneFor: toneForFormValue,
};
const CD_ATTENTION_FIELD: FormFieldDef = {
  key: "clinicalDirectorAttention",
  label: "Clinical director attention",
  kind: "enum",
  required: true,
  options: toOptions(CD_ATTENTION_LABELS),
  toneFor: toneForFormValue,
};

const BIGGEST_WIN_FIELD: FormFieldDef = {
  key: "biggestWin",
  label: "Biggest win this week",
  kind: "text",
  required: false,
};
const BIGGEST_BARRIER_FIELD: FormFieldDef = {
  key: "biggestBarrier",
  label: "Biggest barrier this week",
  kind: "text",
  required: false,
};
const COACHING_FOCUS_FIELD: FormFieldDef = {
  key: "coachingFocus",
  label: "Coaching focus",
  kind: "textarea",
  required: false,
};
const ACTION_PLAN_FIELD: FormFieldDef = {
  key: "actionPlan",
  label: "Action plan",
  kind: "textarea",
  required: false,
  hint: "plain text, not tasks",
};

/**
 * Onboarding / intensive-support weekly form (v1): all five enums + four free
 * texts, in comp order.
 */
const WEEKLY_ONBOARDING_V1: FormFieldDef[] = [
  STATUS_FIELD,
  TRAJECTORY_FIELD,
  CONFIDENCE_FIELD,
  COUNSELING_FIELD,
  CD_ATTENTION_FIELD,
  BIGGEST_WIN_FIELD,
  BIGGEST_BARRIER_FIELD,
  COACHING_FOCUS_FIELD,
  ACTION_PLAN_FIELD,
];

/**
 * Ongoing-supervision weekly form (v1): same as onboarding minus Counseling
 * development (spec §8 — Celeste's final ongoing field set is an open input;
 * v1 must support the variant boundary).
 */
const WEEKLY_ONGOING_V1: FormFieldDef[] = [
  STATUS_FIELD,
  TRAJECTORY_FIELD,
  CONFIDENCE_FIELD,
  CD_ATTENTION_FIELD,
  BIGGEST_WIN_FIELD,
  BIGGEST_BARRIER_FIELD,
  COACHING_FOCUS_FIELD,
  ACTION_PLAN_FIELD,
];

type VariantVersionKey = `${FormVariant}:${number}`;

const WEEKLY_FORM_REGISTRY: Partial<Record<VariantVersionKey, FormFieldDef[]>> = {
  "onboarding_intensive_support:1": WEEKLY_ONBOARDING_V1,
  "ongoing_supervision:1": WEEKLY_ONGOING_V1,
};

/**
 * Field defs for a given form coordinate. Weekly forms are the only captured
 * type in v1; monthly checkpoints have no field set (list/flyover render a
 * minimal shell). Returns [] for unknown coordinates.
 */
export function getFormFields(
  formType: FormType,
  formVariant: FormVariant,
  formVersion: number,
): FormFieldDef[] {
  if (formType !== "weekly_form") return [];
  return WEEKLY_FORM_REGISTRY[`${formVariant}:${formVersion}`] ?? [];
}

/** Enum field defs only (the required, queryable columns for a form). */
export function getFormEnumFields(
  formType: FormType,
  formVariant: FormVariant,
  formVersion: number,
): FormFieldDef[] {
  return getFormFields(formType, formVariant, formVersion).filter(
    (field) => field.kind === "enum",
  );
}

/** The RD's support track picks the default variant for a new snapshot. */
export function defaultVariantForTrack(track: FormSupportTrack): FormVariant {
  switch (track) {
    case "onboarding_intensive_support":
      return "onboarding_intensive_support";
    case "ongoing_supervision":
      return "ongoing_supervision";
  }
}

/** Human label for a support track / variant (both share the enum values). */
export function variantLabel(variant: FormVariant): string {
  switch (variant) {
    case "onboarding_intensive_support":
      return "Onboarding — intensive support";
    case "ongoing_supervision":
      return "Ongoing supervision";
  }
}

/** Human label for a support track. */
export function supportTrackLabel(track: FormSupportTrack): string {
  return variantLabel(track);
}

// --- Derived row title + change line (week-over-week movement) -------------

/**
 * Minimal snapshot shape the derivation helpers need. Both the full
 * `FormSnapshotData` projection and lighter row-list rows satisfy it.
 */
export interface DerivableSnapshot {
  formType: FormType;
  status: FormStatus | null;
  trajectory: FormTrajectory | null;
  overallConfidence: FormConfidence | null;
  response: FormResponseJson;
}

// Severity ranks — higher is worse. Used to phrase direction ("Down"/"Up").
const STATUS_RANK: Record<FormStatus, number> = {
  maintenance_rock_star: 0,
  active_growth: 1,
  watch_list: 2,
  at_risk: 3,
};
const CONFIDENCE_RANK: Record<FormConfidence, number> = {
  high: 0,
  moderate: 1,
  low: 2,
  concerning: 3,
};
const TRAJECTORY_RANK: Record<FormTrajectory, number> = {
  improving: 0,
  stable: 1,
  declining: 2,
};

function excerpt(text: string | null | undefined, maxLength = 64): string {
  if (!text) return "";
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  const clipped = normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd();
  const lastSpace = clipped.lastIndexOf(" ");
  return `${lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped}…`;
}

/**
 * A concise week-over-week movement descriptor for the snapshot row title.
 * Priority: status change, then confidence change, then trajectory change.
 * Returns null when there is no prior weekly snapshot or nothing moved.
 */
function movementDescriptor(
  snapshot: DerivableSnapshot,
  prior: DerivableSnapshot | null | undefined,
): string | null {
  if (snapshot.formType !== "weekly_form") return null;
  if (!prior || prior.formType !== "weekly_form") return null;

  if (
    snapshot.status &&
    prior.status &&
    snapshot.status !== prior.status
  ) {
    const worse = STATUS_RANK[snapshot.status] > STATUS_RANK[prior.status];
    return `${worse ? "Down" : "Up"} from ${formValueLabel(prior.status)}`;
  }

  if (
    snapshot.overallConfidence &&
    prior.overallConfidence &&
    snapshot.overallConfidence !== prior.overallConfidence
  ) {
    const worse =
      CONFIDENCE_RANK[snapshot.overallConfidence] >
      CONFIDENCE_RANK[prior.overallConfidence];
    return worse ? "Confidence dropped" : "Confidence up";
  }

  if (
    snapshot.trajectory &&
    prior.trajectory &&
    snapshot.trajectory !== prior.trajectory
  ) {
    return `Now ${formValueLabel(snapshot.trajectory)}`;
  }

  return null;
}

/**
 * The bold lead for a snapshot list row. When status / confidence / trajectory
 * moved vs the prior weekly snapshot, it's the movement descriptor
 * ("Down from Active Growth", "Confidence dropped"); otherwise a Biggest-win
 * excerpt; otherwise an empty string (caller falls back to a period label).
 */
export function deriveRowTitle(
  snapshot: DerivableSnapshot,
  prior?: DerivableSnapshot | null,
): string {
  const movement = movementDescriptor(snapshot, prior);
  if (movement) return movement;
  return excerpt(snapshot.response.biggestWin);
}

/**
 * The quiet change line shown in the flyover under the enum block, e.g.
 * "Down from Active Growth — confidence was Moderate the week before." Combines
 * a status-direction clause with a confidence clause (and, absent a status
 * move, a trajectory clause). Returns null when there is no movement to report.
 */
export function deriveChangeLine(
  snapshot: DerivableSnapshot,
  prior?: DerivableSnapshot | null,
): string | null {
  if (snapshot.formType !== "weekly_form") return null;
  if (!prior || prior.formType !== "weekly_form") return null;

  const segments: string[] = [];
  const statusChanged =
    !!snapshot.status &&
    !!prior.status &&
    snapshot.status !== prior.status;

  if (statusChanged) {
    const worse = STATUS_RANK[snapshot.status!] > STATUS_RANK[prior.status!];
    segments.push(`${worse ? "Down" : "Up"} from ${formValueLabel(prior.status)}`);
  }

  if (
    snapshot.overallConfidence &&
    prior.overallConfidence &&
    snapshot.overallConfidence !== prior.overallConfidence
  ) {
    segments.push(
      `confidence was ${formValueLabel(prior.overallConfidence)} the week before`,
    );
  }

  if (
    !statusChanged &&
    snapshot.trajectory &&
    prior.trajectory &&
    snapshot.trajectory !== prior.trajectory
  ) {
    const worse =
      TRAJECTORY_RANK[snapshot.trajectory] > TRAJECTORY_RANK[prior.trajectory];
    segments.push(
      `trajectory ${worse ? "slipped to" : "moved to"} ${formValueLabel(snapshot.trajectory)}`,
    );
  }

  if (segments.length === 0) return null;
  return `${segments.join(" — ")}.`;
}

/** The four narrative fields with resolved values, in display order. */
export function narrativeEntries(
  response: FormResponseJson,
): { key: FormNarrativeKey; label: string; value: string }[] {
  const byKey: Record<FormNarrativeKey, string> = {
    biggestWin: "Biggest win this week",
    biggestBarrier: "Biggest barrier this week",
    coachingFocus: "Coaching focus",
    actionPlan: "Action plan",
  };
  return (Object.keys(byKey) as FormNarrativeKey[]).map((key) => ({
    key,
    label: byKey[key],
    value: (response[key] ?? "").toString(),
  }));
}
